import React, { ChangeEvent } from 'react';
import { useFormikContext, Field, useField } from 'formik';
import {
    FormControl,
    FormControlLabel,
    FormHelperText,
    Radio,
    RadioGroup as MUIRadioGroup,
    Typography,
    Stack,
} from '@mui/material';
import { StepperControls, StepWrapper, StyledCardSection, TextField } from '@fnol/library/src/components';
import { checkEmail, common, getApplicationId, getIsSalesForce, hasValueAndIsSalesForce } from '@fnol/library/src/lib';
import {
    translations,
    nameMaxLength,
    surnameMaxLength,
    questionMaxLength,
    phoneMaxLength,
    question50MaxLength,
    question30MaxLength,
} from '../../lib/consts';
import { getShouldDisplayYouDroveField, handleAlphabeticCharactersOnly, checkPhoneNumber } from '../../lib/helpers';
import { Info } from '@mui/icons-material';

type GenericObject = { [key: string]: any };

type Props = {
    navigator: GenericObject[];
};

const PersonalInfoScreen = ({ navigator }: Props) => {
    const [currentStep, { goForward, goBack }] = navigator;
    const { values, setFieldValue, setErrors, setTouched, errors, touched } = useFormikContext<GenericObject>();
    const [youDroveField] = useField('personalYouDrove');
    const [causeField] = useField('cause');
    const [vehicleParkedField] = useField('vehicleParked');
    const [shouldSendLink] = useField('personalShouldSendLink');
    const shouldDisplayYouDroveField = getShouldDisplayYouDroveField(causeField.value, vehicleParkedField.value);
    const shouldDisplayDriversInfoForm = youDroveField?.value === 'false';
    const shouldDisplaySendSmsField = shouldSendLink.value === 'true';

    const handleNext = (): void => {
        let valid = true;
        setTouched(
            {
                personalName: true,
                personalLastName: true,
                personalMobile: true,
                personalEmail: true,
                personalYouDrove: true,
                driverName: true,
                driverLastName: true,
                personalShouldSendLink: true,
                smsContactMobile: true,
            },
            false,
        );
        const existingErrors: GenericObject = {};
        if (values.personalName === '') {
            existingErrors.personalName = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (values.personalLastName === '') {
            existingErrors.personalLastName = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (values.personalMobile === '' && !hasValueAndIsSalesForce(values.personalEmail)) {
            existingErrors.personalMobile = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (values.personalMobile !== '' && !checkPhoneNumber(values.personalMobile)) {
            existingErrors.personalMobile = translations.validationMessages.PHONE_NUMBER_FORMAT;
            valid = false;
        }
        if (values.personalEmail === '' && !hasValueAndIsSalesForce(values.personalMobile)) {
            existingErrors.personalEmail = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (values.personalEmail !== '' && !checkEmail(values.personalEmail)) {
            existingErrors.personalEmail = translations.validationMessages.EMAIL_ADDRESS_FORMAT;
            valid = false;
        }
        if (shouldDisplayYouDroveField && (!values.personalYouDrove || values.personalYouDrove === '')) {
            existingErrors.personalYouDrove = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (shouldDisplayYouDroveField && values.personalYouDrove === 'false') {
            if (values.driverName === '') {
                existingErrors.driverName = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
            if (values.driverLastName === '') {
                existingErrors.driverLastName = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
        }
        if (!values.personalShouldSendLink || values.personalShouldSendLink === '') {
            existingErrors.personalShouldSendLink = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (values.personalShouldSendLink === 'true') {
            if (values.smsContactMobile === '') {
                existingErrors.smsContactMobile = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
            if (!checkPhoneNumber(values.smsContactMobile)) {
                existingErrors.smsContactMobile = translations.validationMessages.PHONE_NUMBER_FORMAT;
                valid = false;
            }
        }
        setErrors(existingErrors);

        if (valid) {
            goForward();
        }
    };

    const handleGoBack = (): void => {
        goBack();
    };

    const handleChangeYouDrove = (event: ChangeEvent<HTMLInputElement>) => {
        setFieldValue('driverName', '');
        setFieldValue('driverLastName', '');
        setFieldValue('driverAddress', '');
        setFieldValue('driverCity', '');
        setFieldValue('driverMobile', '+3816');
        setFieldValue('personalYouDrove', event.target.value);
    };

    const handleChangePersonalShouldSendLink = (event: ChangeEvent<HTMLInputElement>) => {
        setFieldValue('smsContactMobile', '+3816');
        setFieldValue('personalShouldSendLink', event.target.value);
    };

    const RadioGroup = (params: any) => {
        return (
            <MUIRadioGroup row {...params}>
                <FormControlLabel value={true} control={<Radio />} label="Da" />
                <FormControlLabel value={false} control={<Radio />} label="Ne" />
            </MUIRadioGroup>
        );
    };

    return (
        <StepWrapper>
            <StyledCardSection>
                <Typography style={{ fontWeight: 'bold', fontSize: '18px' }} variant="caption">
                    {translations.personalInfo.CLAIM_SUBMITTED_BY}
                </Typography>
                <FormControl>
                    <Field
                        name="personalName"
                        label={translations.personalInfo.NAME}
                        onKeyDown={handleAlphabeticCharactersOnly}
                        as={TextField}
                        inputProps={{
                            maxLength: nameMaxLength,
                        }}
                        error={!!(touched.personalName && errors.personalName)}
                    />
                    {touched.personalName && errors.personalName ? (
                        <FormHelperText error={true}>
                            <>{errors.personalName}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
                <FormControl>
                    <Field
                        name="personalLastName"
                        label={translations.personalInfo.LAST_NAME}
                        onKeyDown={handleAlphabeticCharactersOnly}
                        as={TextField}
                        inputProps={{
                            maxLength: surnameMaxLength,
                        }}
                        error={!!(touched.personalLastName && errors.personalLastName)}
                    />
                    {touched.personalLastName && errors.personalLastName ? (
                        <FormHelperText error={true}>
                            <>{errors.personalLastName}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>

                {getIsSalesForce(getApplicationId()) && (
                    <Stack direction="row" alignItems="top" spacing={2} marginTop={2} marginBottom={3}>
                        <Info color="primary" />
                        <Typography variant="body2" paddingTop={0.5}>
                            {translations.validationMessages.CONTACT_REQUIRED}
                        </Typography>
                    </Stack>
                )}

                <FormControl>
                    <Field
                        name="personalMobile"
                        label={
                            hasValueAndIsSalesForce(values.personalEmail)
                                ? translations.personalInfo.MOBILE
                                : translations.personalInfo.MOBILE_STAR
                        }
                        as={TextField}
                        inputProps={{
                            maxLength: phoneMaxLength,
                        }}
                        error={!!(touched.personalMobile && errors.personalMobile)}
                    />
                    {touched.personalMobile && errors.personalMobile ? (
                        <FormHelperText error={true}>
                            <>{errors.personalMobile}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>

                <FormControl>
                    <Field
                        name="personalEmail"
                        label={
                            hasValueAndIsSalesForce(values.personalMobile)
                                ? translations.personalInfo.EMAIL
                                : translations.personalInfo.EMAIL_STAR
                        }
                        type="email"
                        as={TextField}
                        inputProps={{
                            maxLength: questionMaxLength,
                        }}
                        error={!!(touched.personalEmail && errors.personalEmail)}
                    />
                    {touched.personalEmail && errors.personalEmail ? (
                        <FormHelperText error={true}>
                            <>{errors.personalEmail}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
                {shouldDisplayYouDroveField ? (
                    <>
                        <FormControl>
                            <Typography style={{ fontSize: '16px' }} variant="caption">
                                {translations.personalInfo.DID_YOU_DRIVE}
                            </Typography>

                            <Field name="personalYouDrove" as={RadioGroup} onChange={handleChangeYouDrove} />
                            {touched.personalYouDrove && errors.personalYouDrove ? (
                                <FormHelperText error={true}>
                                    <>{errors.personalYouDrove}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    </>
                ) : null}
            </StyledCardSection>
            <>
                {shouldDisplayDriversInfoForm ? (
                    <StyledCardSection>
                        <Typography style={{ fontWeight: 'bold', fontSize: '16px' }} variant="caption">
                            {translations.driverInfo.DRIVER_INFO}
                        </Typography>
                        <FormControl>
                            <Field
                                name="driverName"
                                placeholder={translations.driverInfo.NAME}
                                onKeyDown={handleAlphabeticCharactersOnly}
                                as={TextField}
                                inputProps={{
                                    maxLength: nameMaxLength,
                                }}
                                error={!!(touched.driverName && errors.driverName)}
                            />
                            {touched.driverName && errors.driverName ? (
                                <FormHelperText error={true}>
                                    <>{errors.driverName}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl>
                            <Field
                                name="driverLastName"
                                placeholder={translations.driverInfo.LAST_NAME}
                                onKeyDown={handleAlphabeticCharactersOnly}
                                as={TextField}
                                inputProps={{
                                    maxLength: surnameMaxLength,
                                }}
                                error={!!(touched.driverLastName && errors.driverLastName)}
                            />
                            {touched.driverLastName && errors.driverLastName ? (
                                <FormHelperText error={true}>
                                    <>{errors.driverLastName}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <Field
                            name="driverAddress"
                            placeholder={translations.driverInfo.ADDRESS}
                            as={TextField}
                            inputProps={{
                                maxLength: question50MaxLength,
                            }}
                        />
                        <Field
                            name="driverCity"
                            placeholder={translations.driverInfo.CITY}
                            as={TextField}
                            inputProps={{
                                maxLength: question30MaxLength,
                            }}
                        />
                        <Field
                            name="driverMobile"
                            placeholder={translations.driverInfo.MOBILE}
                            as={TextField}
                            inputProps={{
                                maxLength: phoneMaxLength,
                            }}
                        />
                    </StyledCardSection>
                ) : null}
            </>
            <StyledCardSection>
                <FormControl>
                    <Typography style={{ fontSize: '16px' }} variant="caption">
                        {translations.personalInfo.SHOULD_WE_SEND_LINK}
                    </Typography>

                    <Field
                        name="personalShouldSendLink"
                        as={RadioGroup}
                        onChange={handleChangePersonalShouldSendLink}
                    />
                    {touched.personalShouldSendLink && errors.personalShouldSendLink ? (
                        <FormHelperText error={true}>
                            <>{errors.personalShouldSendLink}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
                {shouldDisplaySendSmsField ? (
                    <>
                        <Typography style={{ fontSize: '16px' }} variant="caption">
                            {translations.personalInfo.LINK_RECEPIENT}
                        </Typography>
                        <FormControl>
                            <Field
                                name="smsContactMobile"
                                label={translations.personalInfo.MOBILE}
                                as={TextField}
                                inputProps={{
                                    maxLength: phoneMaxLength,
                                }}
                                error={!!(touched.smsContactMobile && errors.smsContactMobile)}
                            />
                            {touched.smsContactMobile && errors.smsContactMobile ? (
                                <FormHelperText error={true}>
                                    <>{errors.smsContactMobile}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    </>
                ) : null}
            </StyledCardSection>
            <StepperControls onPrevious={handleGoBack} onNext={handleNext} onNextLabel={common.NEXT} />
        </StepWrapper>
    );
};

export default PersonalInfoScreen;
